import React from 'react'
import DataDatetime from 'syft-acp-core/components/EditableTable/DataDatetime'
import * as structureTypes from 'syft-acp-core/components/EditableTable'
import { fullLocalDateTimeFormat } from 'syft-acp-core/components/FormattedDateTime'
import { IconClock } from 'syft-acp-core/components/IconClock'
import { useFormatDateTime } from 'syft-acp-core/lib/hooks'
import { formatRelative } from 'syft-acp-util/formatting'
import { Information as InformationIcon } from '@indeed/ifl-icons'
import { noop } from 'lodash-es'
import { Box, Tag, Tooltip } from '@indeed/ifl-components'

import { Data } from './types'

export const EligibleWorkers = ({
  count,
  lastUpdate,
  timezone,
}: {
  count: number
  lastUpdate: string
  timezone: string
}) => {
  return (
    <Box as="div" sx={{ display: 'flex' }}>
      <Box as="div">{`${count} worker${count === 1 ? '' : 's'} eligible (Last update`}</Box>
      <Box as="div" sx={{ marginLeft: '-8px', marginTop: '-8px' }}>
        <DataDatetime parameters={{ timeZone: timezone }} value={lastUpdate} data={{}} onChange={noop} />
      </Box>
      <Box as="div">{' )'}</Box>
    </Box>
  )
}

// The venue setting is what puts a job on blast, so the switch-on time answers "how long has this
// venue been on blast offers" right where the tag says it is. Older venues switched on before the
// timestamp existed have none, so the tag stands alone.
//
// The date and its relative time are composed here rather than delegated to DataDatetime: that
// component's read-only markup carries the editable-table cell padding, which only lines up next to
// a tag once it is cancelled out with negative margins.
export const BlastOffersTag = ({ enabledAt, timezone }: { enabledAt?: string | null; timezone?: string }) => {
  const [switchedOnAt] = useFormatDateTime(enabledAt ?? undefined, {
    format: { ...fullLocalDateTimeFormat, timeZone: timezone },
  })

  return (
    <Box as="div" sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      <Tooltip label="Blast offers the whole eligible pool at once instead of in batches. A candidate pool of 0 and no next-offer time are expected.">
        <Tag status="info">Blast offers</Tag>
      </Tooltip>
      {enabledAt ? (
        <>
          <Box as="span">on since {switchedOnAt}</Box>
          <Box as="span" className="secondary" sx={{ display: 'flex', alignItems: 'center' }}>
            <IconClock />
            {formatRelative(new Date(enabledAt))}
          </Box>
        </>
      ) : null}
    </Box>
  )
}

export const structure = [
  [
    'offering_stats.workers_offered',
    {
      header: 'Workers offered',
      defaultValue: '—',
    },
  ],
  [
    'select_offer_type',
    {
      header: (
        <>
          Selected offer type{' '}
          <Tooltip label="This section reflects the stats for the latest offer type selected.">
            <InformationIcon size="sm" sx={{ verticalAlign: 'top' }} color="grey" />
          </Tooltip>
        </>
      ),
      // When blast offers is on, the pool / next-offer rows below read 0 / — by design (the whole
      // eligible pool is offered at once), so surface the method here to explain those values.
      calc: (data: Data) =>
        data?.offering_stats?.blast_offers_active ? (
          <BlastOffersTag enabledAt={data?.venue?.blast_offers_enabled_at} timezone={data?.venue?.timezone} />
        ) : null,
    },
  ],
  [
    'offering_stats.last_time_it_run',
    {
      header: <div style={{ marginLeft: 10 }}>Job candidate pool size</div>,
      calc: (data: Data) => {
        if (
          (data?.offering_stats?.workers_available || data?.offering_stats?.workers_available === 0) &&
          data?.offering_stats?.workers_available_updated_at
        ) {
          return (
            <EligibleWorkers
              timezone={data?.venue?.timezone}
              count={data?.offering_stats?.workers_available}
              lastUpdate={data?.offering_stats?.workers_available_updated_at}
            />
          )
        }

        return '—'
      },
      parameters: (data: Data) => ({ timeZone: data?.venue?.timezone }),
    },
  ],
  [
    'offering_stats.next_offer_at',
    {
      header: <div style={{ marginLeft: 10 }}>Next offers will be sent at</div>,
      parameters: (data: Data) => ({ timeZone: data?.venue?.timezone }),
      type: structureTypes.TYPE_TIMESTAMP,
      defaultValue: '—',
    },
  ],
  [
    'offering_stats.fill_automation_active',
    {
      header: (
        <>
          Fill automation status{' '}
          <Tooltip label="This section shows if the fill automation for the worker pool has kicked in and the remaining worker supply eligible for offers.">
            <InformationIcon size="sm" sx={{ verticalAlign: 'top' }} color="grey" />
          </Tooltip>
        </>
      ),
      calc: (data: Data) => {
        return data?.offering_stats?.fill_automation_active ? 'Active' : 'Not active'
      },
    },
  ],
  [
    'offering_stats.fill_automation_workers_available',
    {
      header: <div style={{ marginLeft: 10 }}>Job candidate pool size</div>,
      calc: (data: Data) => {
        if (
          (data?.offering_stats?.fill_automation_workers_available ||
            data?.offering_stats?.fill_automation_workers_available === 0) &&
          data?.offering_stats?.fill_automation_workers_available_updated_at
        ) {
          return (
            <EligibleWorkers
              timezone={data?.venue?.timezone}
              count={data?.offering_stats?.fill_automation_workers_available}
              lastUpdate={data?.offering_stats?.fill_automation_workers_available_updated_at}
            />
          )
        }

        return '—'
      },
      parameters: (data: Data) => ({ timeZone: data?.venue?.timezone }),
    },
  ],
]
